import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Export and deletion screens (ADR tenant-export-deletion-and-consent). Both are a tenant
// admin's: for a personal product that is the person, for a team product the workspace's admin.
// `subject` only changes the words ("your account" / "this workspace").
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, Checkbox, FormControlLabel, KeyValueSkeleton, Stack, TextField, Typography, useToast, } from '@platform/design-system';
import { describeError, toAppError } from "../http/errors.js";
import { longDate } from "./copy.js";
import { useCancelDeletion, useExportStatus, useFreshExportLink, useRequestDeletion, useRequestExport, useWorkspace, } from "./queries.js";
import { useSession } from "../auth/session.js";
const WORDS = {
    account: { the: 'your account' },
    workspace: { the: 'this workspace' },
};
// ---------------------------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------------------------
function rememberedExport(tenantId) {
    try {
        return tenantId ? window.localStorage.getItem(`export:${tenantId}`) : null;
    }
    catch {
        return null;
    }
}
function remember(tenantId, exportId) {
    try {
        if (!tenantId)
            return;
        if (exportId)
            window.localStorage.setItem(`export:${tenantId}`, exportId);
        else
            window.localStorage.removeItem(`export:${tenantId}`);
    }
    catch {
        // Private windows and blocked storage: the panel still works, it just forgets on reload.
    }
}
/** Hand the browser a link to download. Replaced in tests. */
export function startDownload(url) {
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    link.download = '';
    document.body.appendChild(link);
    link.click();
    link.remove();
}
/**
 * Take a copy of everything held: ask for it, watch it being prepared, then download it. The link
 * the server gives lasts a few minutes, so the button asks for a fresh one when it is pressed.
 */
export function DataExportPanel({ subject, onDownload = startDownload, pollMs, }) {
    const { principal } = useSession();
    const tenantId = principal?.tenantId ?? null;
    const toast = useToast();
    const [exportId, setExportId] = useState(() => rememberedExport(tenantId));
    const [notice, setNotice] = useState(null);
    const request = useRequestExport();
    const status = useExportStatus(exportId, pollMs);
    const fresh = useFreshExportLink();
    const w = WORDS[subject];
    // A copy we remembered but the server no longer knows is forgotten (and not asked about again).
    const lost = status.isError;
    useEffect(() => {
        if (lost)
            remember(tenantId, null);
    }, [lost, tenantId]);
    const state = lost ? undefined : status.data?.status;
    const knownId = lost ? null : exportId;
    const ask = () => {
        setNotice(null);
        request.mutate(undefined, {
            onSuccess: (created) => {
                remember(tenantId, created.exportId);
                setExportId(created.exportId);
            },
            onError: (e) => {
                if (toAppError(e).status === 409)
                    setNotice('A copy is already being prepared. It takes a few minutes. Check back here shortly.');
                else
                    toast.error(describeError(e, 'We couldn’t start that. Try again.'));
            },
        });
    };
    const download = () => {
        if (!knownId)
            return;
        fresh.mutate(knownId, {
            onSuccess: (latest) => {
                if (latest.status === 'ready' && latest.downloadUrl)
                    onDownload(latest.downloadUrl);
                else
                    toast.error('That copy isn’t available any more. Ask for a new one.');
            },
            onError: (e) => toast.error(describeError(e, 'We couldn’t get your download. Try again.')),
        });
    };
    let body;
    if (status.isLoading && knownId)
        body = _jsx(KeyValueSkeleton, { rows: 2 });
    else if (state === 'pending')
        body = (_jsxs(Stack, { spacing: 1, role: "status", children: [_jsx(Typography, { variant: "body1", children: "Preparing your copy\u2026" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "This usually takes a minute or two. You can leave this page and come back; the copy keeps preparing." })] }));
    else if (state === 'ready' && status.data)
        body = (_jsxs(Stack, { spacing: 1.5, children: [_jsx(Typography, { variant: "body1", role: "status", children: "Your copy is ready." }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["It stays available until ", longDate(status.data.expiresAt), ". The download is a single zip file."] }), _jsx(Box, { children: _jsx(Button, { variant: "contained", onClick: download, busy: fresh.isPending, busyLabel: "Getting your download\u2026", children: "Download your data" }) })] }));
    else if (state === 'failed' || state === 'expired')
        body = (_jsxs(Stack, { spacing: 1.5, children: [_jsx(Alert, { severity: state === 'failed' ? 'error' : 'info', children: state === 'failed'
                        ? 'We couldn’t prepare your copy. Nothing has been lost. Try again.'
                        : 'That copy has expired. Ask for a new one.' }), _jsx(Box, { children: _jsx(Button, { onClick: ask, busy: request.isPending, busyLabel: "Starting\u2026", children: "Ask for a new copy" }) })] }));
    else
        body = (_jsxs(Stack, { spacing: 1.5, children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", children: ["Get a copy of everything we hold in ", w.the, ", as a zip file you can keep. It is private to you and is deleted from our side after 7 days."] }), notice && (_jsx(Alert, { severity: "info", role: "status", children: notice })), _jsx(Box, { children: _jsx(Button, { onClick: ask, busy: request.isPending, busyLabel: "Starting\u2026", children: "Ask for a copy of my data" }) })] }));
    return _jsx(Card, { title: "Download your data", children: body });
}
// ---------------------------------------------------------------------------------------------
// Deletion
// ---------------------------------------------------------------------------------------------
/**
 * Delete everything: explained in plain words, confirmed in the page (a tick, and for a team the
 * workspace's name typed), then scheduled 30 days on with the date shown and a way to cancel.
 */
export function DeletionPanel({ subject, confirmName, }) {
    const toast = useToast();
    const workspace = useWorkspace();
    const request = useRequestDeletion();
    const cancel = useCancelDeletion();
    const [opened, setOpened] = useState(false);
    const [understood, setUnderstood] = useState(false);
    const [typed, setTyped] = useState('');
    const first = useRef(null);
    const w = WORDS[subject];
    useEffect(() => {
        if (opened)
            first.current?.focus();
    }, [opened]);
    if (workspace.isLoading)
        return (_jsx(Card, { title: `Delete ${w.the}`, children: _jsx(KeyValueSkeleton, { rows: 2 }) }));
    if (workspace.isError)
        return (_jsx(Card, { title: `Delete ${w.the}`, children: _jsx(Alert, { severity: "warning", action: _jsx(Button, { size: "small", onClick: () => void workspace.refetch(), children: "Try again" }), children: describeError(workspace.error, 'We couldn’t load this just now.') }) }));
    const scheduled = workspace.data?.deletion;
    if (scheduled) {
        return (_jsx(Card, { title: `Delete ${w.the}`, children: _jsxs(Stack, { spacing: 1.5, children: [_jsxs(Alert, { severity: "warning", role: "status", children: [subject === 'account' ? 'Your account' : 'This workspace', " is set to be deleted on", ' ', _jsx("strong", { children: longDate(scheduled.purgeAt) }), "."] }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Until then everything is still here and nothing has been deleted. You can cancel at any time before that day and carry on as before. After that day it can\u2019t be undone." }), _jsx(Box, { children: _jsx(Button, { onClick: () => cancel.mutate(undefined, {
                                onSuccess: () => toast.success('Deletion cancelled'),
                                onError: (e) => toast.error(toAppError(e).status === 409
                                    ? 'The deletion date has passed, so it can’t be cancelled.'
                                    : describeError(e, 'We couldn’t cancel that. Try again.')),
                            }), busy: cancel.isPending, busyLabel: "Cancelling\u2026", children: "Cancel deletion" }) })] }) }));
    }
    const confirmed = understood && (confirmName === undefined || typed.trim() === confirmName);
    const schedule = () => request.mutate(undefined, {
        onSuccess: (done) => {
            setOpened(false);
            setUnderstood(false);
            setTyped('');
            toast.success(`Deletion scheduled for ${longDate(done.purgeAt)}`);
        },
        onError: (e) => toast.error(describeError(e, 'We couldn’t schedule that. Try again.')),
    });
    return (_jsx(Card, { title: `Delete ${w.the}`, children: _jsxs(Stack, { spacing: 1.5, children: [_jsxs(Typography, { variant: "body2", color: "text.secondary", children: [subject === 'account'
                            ? 'You can have your account and everything in it deleted. '
                            : 'An admin can have this workspace and everything in it deleted for everyone. ', "We wait 30 days first: nothing is deleted until then, and you can cancel any time before that. After 30 days it is deleted for good and can\u2019t be brought back."] }), _jsxs(Typography, { variant: "body2", color: "text.secondary", children: [subject === 'account'
                            ? 'Your plan ends at the end of the period you have paid for, and you won’t be charged again. Your sign-in is deleted with your data. '
                            : 'The plan ends at the end of the period already paid for, and no further charges are made. Everyone’s access ends with it. ', "We keep only the invoices for payments made and a record that deletion was requested. Backups age out within 35 days."] }), !opened ? (_jsx(Box, { children: _jsxs(Button, { color: "error", onClick: () => setOpened(true), children: ["Delete ", w.the, "\u2026"] }) })) : (_jsxs(Stack, { spacing: 1.5, component: "form", noValidate: true, "aria-label": `Confirm deleting ${w.the}`, onSubmit: (event) => {
                        event.preventDefault();
                        if (confirmed)
                            schedule();
                    }, children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { slotProps: { input: { ref: first } }, checked: understood, onChange: (event) => setUnderstood(event.target.checked) }), label: `I understand that everything in ${w.the} will be deleted for good after 30 days.` }), confirmName !== undefined && (_jsx(TextField, { label: `Type “${confirmName}” to confirm`, value: typed, onChange: (event) => setTyped(event.target.value), autoComplete: "off" })), _jsxs(Stack, { direction: "row", spacing: 1, children: [_jsx(Button, { type: "submit", variant: "contained", color: "error", disabled: !confirmed, busy: request.isPending, busyLabel: "Scheduling\u2026", children: "Delete in 30 days" }), _jsxs(Button, { onClick: () => {
                                        setOpened(false);
                                        setUnderstood(false);
                                        setTyped('');
                                    }, disabled: request.isPending, children: ["Keep ", w.the] })] })] }))] }) }));
}
//# sourceMappingURL=data-rights-panels.js.map